import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch'
import {
    ElementTreeView,
    FamilyNumberFormat,
    ProjectSettings,
    ProjectSettingsInput,
    UnitsOfMeasure,
} from 'models/projectSettings'
import { defaultProjSettings } from 'pages/ProjectsPage/projectUtils'
import React from 'react'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
            paddingBottom: theme.spacing(0.25),
        },
    },
    dialogCont: {
        '&.MuiDialogContent-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
        fontSize: pxToRem(18),
    },
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: theme.spacing(2),
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            width: pxToRem(490),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    radioGroup: {
        '& .MuiFormControlLabel-root': {
            maxWidth: 400,
            padding: theme.spacing(1.2, 0.4),
        },
        '& .MuiRadio-colorSecondary.Mui-checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '& .MuiRadio-colorSecondary': {
            // color: 'rgba(0, 0, 0, 0.54)',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '&.MuiIconButton-root': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '& .MuiIconButton-colorSecondary': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    },
    radioGroupGridItem: {
        paddingTop: theme.spacing(1),
        borderBottom: '1px solid rgba(201, 201, 201, 0.54)',
    },
    settingTitle: {
        fontSize: pxToRem(13),
        color: 'rgba(0, 0, 0, 0.54)',
        textTransform: 'capitalize',
    },
    dataCollectionContainer: {
        maxWidth: pxToRem(450),
        marginBottom: theme.spacing(1.2),
    },
    secondRadio: {
        width: pxToRem(140),
    },
    firstRadio: {
        width: pxToRem(160),
    },
}))

export interface ProjectSettingsDialogProps {
    isDialogDisplayed: boolean
    handleDialogClose: VoidFunction
    settings: Partial<ProjectSettings> | undefined
    saveSettings: (input: ProjectSettingsInput) => void
}

const ProjectSettingsDialog: React.FC<ProjectSettingsDialogProps> = ({
    isDialogDisplayed,
    handleDialogClose,
    settings,
    saveSettings,
}) => {
    const classes = useStyles()
    const { projectId } = useParams<{ projectId?: string }>()

    const [projectSettings, setProjectSettings] = React.useState<
        Partial<ProjectSettings>
    >((settings as ProjectSettings) ?? defaultProjSettings)

    const [hasChanges, setHasChanges] = React.useState<boolean>(false)

    const updateProjectSettings = (
        propertyName: 'familyNumberFormat' | 'unitOfMeasure' | 'collectData',
        updatedValue: FamilyNumberFormat | UnitsOfMeasure | boolean
    ) => {
        switch (propertyName) {
            case 'familyNumberFormat': {
                setProjectSettings((past) => ({
                    ...past,
                    familyNumberFormat: updatedValue as FamilyNumberFormat,
                }))
                break
            }
            case 'unitOfMeasure': {
                setProjectSettings((past) => ({
                    ...past,
                    unitOfMeasure: updatedValue as UnitsOfMeasure,
                }))
                break
            }
            case 'collectData': {
                setProjectSettings((past) => ({
                    ...past,
                    collectData: updatedValue as boolean,
                }))
                break
            }
        }

        setHasChanges(true)
    }

    const toggleCollectData = () => {
        updateProjectSettings('collectData', !projectSettings.collectData)
    }

    const handleSaveSettings = () => {
        if (hasChanges) {
            saveSettings({
                collectData: projectSettings.collectData as boolean,
                familyNumberFormat: projectSettings.familyNumberFormat as FamilyNumberFormat,
                unitOfMeasure: projectSettings.unitOfMeasure as UnitsOfMeasure,
                elementTreeView: projectSettings.elementTreeView as ElementTreeView,
                projectId: projectId as string,
            })
        }
        handleDialogClose()
    }

    return (
        <Dialog
            open={isDialogDisplayed}
            data-testid="projectSettingsDailog"
            className={classes.warningDialogCont}
            aria-labelledby="project-settings"
        >
            <DialogTitle className={classes.dialogTitle}>
                Project Settings
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <GridContainer direction="column">
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Units of Measure
                        </FormLabel>
                        <RadioGroup
                            value={projectSettings?.unitOfMeasure
                                ?.toString()
                                .toUpperCase()}
                            onChange={(_, value) =>
                                updateProjectSettings(
                                    'unitOfMeasure',
                                    value as UnitsOfMeasure
                                )
                            }
                            className={classes.radioGroup}
                            data-testid="unitsOfMeasureRadioGroup"
                            row
                        >
                            {Object.keys(UnitsOfMeasure).map((unit, index) => {
                                return (
                                    <FormControlLabel
                                        key={`UMRadio-${index}`}
                                        value={unit.toUpperCase()}
                                        classes={{
                                            label: classes.settingTitle,
                                        }}
                                        data-testid={`UMRadio-${index}`}
                                        control={
                                            <Radio disableRipple size="small" />
                                        }
                                        label={
                                            UnitsOfMeasure[
                                                unit as keyof typeof UnitsOfMeasure
                                            ]
                                        }
                                        className={clsx(
                                            index === 0 && classes.firstRadio,
                                            index === 1 && classes.secondRadio
                                        )}
                                    />
                                )
                            })}
                        </RadioGroup>
                    </GridItem>
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Family Number Format
                        </FormLabel>
                        <RadioGroup
                            value={projectSettings?.familyNumberFormat
                                ?.toString()
                                .toUpperCase()}
                            onChange={(_, value) =>
                                updateProjectSettings(
                                    'familyNumberFormat',
                                    value as FamilyNumberFormat
                                )
                            }
                            className={classes.radioGroup}
                            data-testid="familyNumFormatRadioGroup"
                            row
                        >
                            {Object.keys(FamilyNumberFormat).map(
                                (format, index) => {
                                    return (
                                        <FormControlLabel
                                            key={`FamNumberFormat-${index}`}
                                            value={format.toUpperCase()}
                                            classes={{
                                                label: classes.settingTitle,
                                            }}
                                            data-testid={`FMRadio-${index}`}
                                            control={
                                                <Radio
                                                    disableRipple
                                                    size="small"
                                                />
                                            }
                                            label={
                                                FamilyNumberFormat[
                                                    format as keyof typeof FamilyNumberFormat
                                                ]
                                            }
                                            className={clsx(
                                                index === 0 &&
                                                    classes.firstRadio,
                                                index === 1 &&
                                                    classes.secondRadio
                                            )}
                                        />
                                    )
                                }
                            )}
                        </RadioGroup>
                    </GridItem>
                    <GridItem className={classes.radioGroupGridItem}>
                        <GridContainer
                            direction="column"
                            data-testid="dataCollection"
                        >
                            <GridItem>
                                <FormLabel className={classes.settingTitle}>
                                    Data Collection
                                </FormLabel>
                            </GridItem>
                            <GridItem>
                                <GridContainer
                                    spacing={2}
                                    className={classes.dataCollectionContainer}
                                >
                                    <GridItem>
                                        <ToggleSwitch
                                            size="small"
                                            checked={
                                                !projectSettings?.collectData as boolean
                                            }
                                            onChange={toggleCollectData}
                                        ></ToggleSwitch>
                                    </GridItem>
                                    <GridItem>
                                        <Typography
                                            component="span"
                                            className={classes.settingTitle}
                                        >
                                            Project Data collection opt-out for
                                            this project.
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="cancelButton"
                    className={classes.actionButton}
                    onClick={handleDialogClose}
                >
                    Cancel
                </Button>
                <Button
                    data-testid="saveButton"
                    className={classes.actionButton}
                    onClick={handleSaveSettings}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ProjectSettingsDialog
